import { zonedDayKey } from "../overview-move"
import type {
  CalendarBoardExternalBusyInterval,
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
  CalendarRouteTransition,
} from "../../../../../api/kehrblatt/schedule-events"
import { addDays, formatTime, setTime, startOfDay } from "../../../../../_shared/_logic/date-math"
import { availabilityFor } from "../../../../../_shared/_logic/working-hours-view"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../_shared/_logic/communication-presentation"
import type { WorkingHoursByUser } from "../../../../../_shared/_logic/form-state"
import {
  displayScheduleStatusLabel,
  resolveScheduleStatusDefinition,
} from "../../../../../_shared/_logic/status-definitions"
import { buildRichTooltip, tooltipText } from "../../../../../_shared/_logic/rich-tooltips"
import { formatPropertyUnitLabel } from "../../../../../_shared/_logic/unit-labels"
import { internalOrderTypeLabel } from "../../../../../_shared/_logic/order-type-labels"
import type {
  BoardColumn,
  BoardEvent,
  BoardExternalBusyInterval,
  BoardRouteEdge,
  EmployeeOption,
} from "../calendar-types"

const joinAddressParts = (...parts: (string | null | undefined)[]) =>
  parts
    .map((part) => `${part ?? ""}`.trim())
    .filter(Boolean)
    .join(" ")

export const formatPropertyAddress = (property?: CalendarBoardPropertyRef) => {
  if (!property) return ""

  const streetLine = joinAddressParts(property.street, property.house_number)
  const cityLine = joinAddressParts(property.zip, property.city)

  return [streetLine, cityLine].filter(Boolean).join(", ")
}

const formatLocalDayKey = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const formatBoardEventAddress = (
  property?: CalendarBoardPropertyRef,
  workOrder?: CalendarBoardWorkOrderRef,
) => {
  const address = formatPropertyAddress(property)
  const unitLabel = workOrder?.property_unit_label
    ? formatPropertyUnitLabel(workOrder.property_unit_label)
    : ""

  if (!address) return unitLabel

  return unitLabel ? `${address} - ${unitLabel}` : address
}

const STALE_APPOINTMENT_TITLE_PREFIX =
  /^\s*\((?:UNBESTÄTIGT|BESTÄTIGT|ERLEDIGT|UNANGEKÜNDIGT|IN ABSTIMMUNG|IN ANKÜNDIGUNG|ANKÜNDIGUNG FEHLGESCHLAGEN|UNGESICHERT ANGEKÜNDIGT|GELESEN|BRIEF ZUGESTELLT|GESICHERT ANGEKÜNDIGT|ABGESAGT|STORNIERT|RESERVIERT)\)\s*/i
const GENERATED_ID_TITLE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const deriveTitle = (event: ScheduleEvent, workOrder?: CalendarBoardWorkOrderRef) => {
  const eventTitle =
    event.event_type === "APPOINTMENT"
      ? `${event.title ?? ""}`.replace(STALE_APPOINTMENT_TITLE_PREFIX, "").trim()
      : `${event.title ?? ""}`.trim()
  const workOrderTitle = internalOrderTypeLabel(workOrder) ?? ""

  if (event.event_type === "APPOINTMENT") {
    if (workOrderTitle) return workOrderTitle

    if (eventTitle && !GENERATED_ID_TITLE_PATTERN.test(eventTitle)) return eventTitle

    return "Termin"
  }

  if (eventTitle) return eventTitle

  if (workOrderTitle) return workOrderTitle

  return "Blocker"
}

const formatTravelMinutesLabel = (minutes: number | null | undefined) => {
  if (typeof minutes !== "number" || Number.isNaN(minutes)) return "nicht berechenbar"

  if (minutes <= 0) return "0 min"

  if (minutes < 1) return "< 1 min"

  return `ca. ${Math.round(minutes)} min`
}

const formatRoundedTravelMinutesLabel = (minutes: number | null | undefined) => {
  if (typeof minutes !== "number" || Number.isNaN(minutes)) return "nicht berechenbar"

  return `${Math.max(0, Math.round(minutes))} min`
}

const buildRouteTransitionTooltip = (item: CalendarRouteTransition) => {
  return buildRichTooltip([
    item.missing_coordinates
      ? tooltipText("Die Fahrtzeit ist nicht berechenbar, weil Ortsangaben fehlen.")
      : tooltipText(
          `Geschätzte Fahrtzeit: ${formatTravelMinutesLabel(item.estimated_travel_minutes)}. Für die Planung sind ${formatRoundedTravelMinutesLabel(item.rounded_travel_minutes)} vorgesehen.`,
        ),
  ])
}

export const assignParallelLanes = (events: BoardEvent[]) => {
  const hardEvents = events
    .filter((item) => item.event.flex_type !== "SOFT")
    .sort((a, b) => {
      const startDelta = a.start.getTime() - b.start.getTime()

      if (startDelta !== 0) return startDelta

      const endDelta = a.end.getTime() - b.end.getTime()

      if (endDelta !== 0) return endDelta

      return String(a.event.id).localeCompare(String(b.event.id))
    })

  let maxLaneCount = 1
  let overlapGroup: BoardEvent[] = []
  let groupEndTs = Number.NEGATIVE_INFINITY

  const flushGroup = () => {
    if (!overlapGroup.length) return

    const laneEndTs: number[] = []
    for (const item of overlapGroup) {
      const startTs = item.top ?? item.start.getTime()
      const laneIndex = laneEndTs.findIndex((endTs) => endTs <= startTs)
      const assignedLaneIndex = laneIndex >= 0 ? laneIndex : laneEndTs.length
      laneEndTs[assignedLaneIndex] =
        item.top != null && item.height != null ? item.top + item.height : item.end.getTime()
      item.laneIndex = assignedLaneIndex
    }

    const laneCount = Math.max(1, laneEndTs.length)
    maxLaneCount = Math.max(maxLaneCount, laneCount)
    for (const item of overlapGroup) {
      item.laneCount = laneCount
      item.laneLeftPct = ((item.laneIndex ?? 0) / laneCount) * 100
      item.laneWidthPct = 100 / laneCount
    }
    overlapGroup = []
    groupEndTs = Number.NEGATIVE_INFINITY
  }

  for (const item of hardEvents) {
    const startTs = item.top ?? item.start.getTime()
    const endTs =
      item.top != null && item.height != null ? item.top + item.height : item.end.getTime()

    if (overlapGroup.length && startTs >= groupEndTs) {
      flushGroup()
    }

    overlapGroup.push(item)
    groupEndTs = Math.max(groupEndTs, endTs)
  }
  flushGroup()

  return maxLaneCount
}

export type BuildColumnsInput = {
  timezone?: string
  targetEmployeeIds: string[]
  visibleDays: Date[]
  eventsByEmployee: Record<string, ScheduleEvent[]>
  externalBusyIntervalsByEmployee?: Record<string, CalendarBoardExternalBusyInterval[]>
  routeTransitions?: CalendarRouteTransition[]
  employees: EmployeeOption[]
  calendarSyncEnabledEmployeeIds?: Set<string>
  employeeColors: Record<string, string>
  workOrderById: Record<string, CalendarBoardWorkOrderRef>
  propertyById: Record<string, CalendarBoardPropertyRef>
  contactOrderByEventId: Record<string, ContactOrder>
  dimmedEventIds?: Set<string>
  workingHoursByUser: WorkingHoursByUser
  now: Date
  dayStartHour: number
  dayEndHour: number
  slotMinutes: number
  slotHeight: number
  minEventHeight: number
  eventTypeColors: Record<ScheduleEventType, string>
  planningFlagLabels: Record<ScheduleEventPlanningFlag, string>
  statusLabels: Record<ScheduleEventStatus, string>
}

export const buildDayColumns = ({
  timezone,
  targetEmployeeIds,
  visibleDays,
  eventsByEmployee,
  externalBusyIntervalsByEmployee = {},
  routeTransitions = [],
  employees,
  calendarSyncEnabledEmployeeIds,
  employeeColors,
  workOrderById,
  propertyById,
  contactOrderByEventId,
  dimmedEventIds = new Set<string>(),
  workingHoursByUser,
  now,
  dayStartHour,
  dayEndHour,
  slotMinutes,
  slotHeight,
  minEventHeight,
  eventTypeColors,
  planningFlagLabels,
  statusLabels,
}: BuildColumnsInput): BoardColumn[] => {
  if (!targetEmployeeIds.length) return []

  const nowTs = now.getTime()
  const zonedRanges = new Map<string, [string, string]>()
  const overlapsDay = (key: string, start: Date, end: Date, dayStart: Date, dayFinish: Date) => {
    if (!timezone) return end > dayStart && start < dayFinish

    const rangeKey = `${start.getTime()}:${end.getTime()}`
    let range = zonedRanges.get(rangeKey)

    if (!range) {
      range = [zonedDayKey(start, timezone), zonedDayKey(new Date(end.getTime() - 1), timezone)]
      zonedRanges.set(rangeKey, range)
    }

    return key >= range[0] && key <= range[1]
  }

  return visibleDays.flatMap((day) => {
    const dayKey = formatLocalDayKey(day)
    const dayStart = startOfDay(day)
    const dayFinish = addDays(dayStart, 1)
    const viewStart = setTime(day, dayStartHour, 0)
    const viewEnd = setTime(day, dayEndHour, 0)

    return targetEmployeeIds.map((employeeId) => {
      const availability = availabilityFor(workingHoursByUser, employeeId, day)
      const events = eventsByEmployee[employeeId] ?? []
      const timedEvents: BoardEvent[] = []
      const externalBusyIntervals: BoardExternalBusyInterval[] = []

      for (const event of events) {
        const isSoft = event.flex_type === "SOFT"
        const start =
          isSoft && event.timeframe_start_at
            ? new Date(event.timeframe_start_at)
            : new Date(event.start_at)
        const end =
          isSoft && event.timeframe_end_at
            ? new Date(event.timeframe_end_at)
            : new Date(event.end_at)

        if (!overlapsDay(dayKey, start, end, dayStart, dayFinish)) continue

        const workOrder = event.work_order_id ? workOrderById[event.work_order_id] : undefined
        const propertyId = event.property_id ?? workOrder?.property_id ?? null
        const property = propertyId ? propertyById[propertyId] : undefined
        const contactOrder = contactOrderByEventId[event.id] ?? undefined
        const locked =
          event.event_type === "APPOINTMENT" &&
          (Boolean(event.contact_order_id) ||
            Boolean(contactOrder) ||
            ["ANNOUNCED", "CONFIRMED", "COMPLETED"].includes(event.status))
        const color = locked
          ? "var(--error-200, #b91c1c)"
          : (eventTypeColors[event.event_type] ?? "var(--kontext-primary)")
        const timeLabel = `${formatTime(start)} - ${formatTime(end)}`
        const title = deriveTitle(event, workOrder)
        const addressLabel = formatBoardEventAddress(property, workOrder)
        const subtitle = workOrder?.order_number
          ? `#${workOrder.order_number}`
          : property
            ? addressLabel
            : ""
        const baseStatusLabel =
          statusLabels[event.status] ?? displayScheduleStatusLabel(event.status)
        const communication =
          event.event_type === "APPOINTMENT"
            ? buildScheduleEventCommunicationTagPresentation({ status: event.status, event })
            : null
        const statusLabel = communication?.label ?? baseStatusLabel
        const base: BoardEvent = {
          event,
          workOrder,
          property,
          contactOrder,
          locked,
          start,
          end,
          isPast: start.getTime() < nowTs,
          color,
          timeLabel,
          statusLabel,
          statusDefinition:
            communication?.definition ?? resolveScheduleStatusDefinition(event.status, statusLabel),
          planningFlag: event.planning_flag,
          planningFlagLabel: event.planning_flag
            ? planningFlagLabels[event.planning_flag]
            : undefined,
          title,
          addressLabel,
          subtitle: subtitle || event.description || undefined,
          dimmed: dimmedEventIds.has(event.id),
        }

        timedEvents.push(base)
      }

      for (const interval of externalBusyIntervalsByEmployee[employeeId] ?? []) {
        const start = new Date(interval.start_at)
        const end = new Date(interval.end_at)

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) continue

        if (!overlapsDay(dayKey, start, end, dayStart, dayFinish)) continue

        externalBusyIntervals.push({ ...interval, start, end, top: 0, height: 0 })
      }

      const parallelLaneCount = 1
      timedEvents.sort((a, b) => a.start.getTime() - b.start.getTime())
      const routeEdges = buildRouteEdges({
        transitions: routeTransitions,
        employeeId,
        dayKey,
      })

      return {
        id: `${employeeId}-${dayKey}`,
        day,
        employee: employees.find((emp) => emp.id === employeeId),
        calendarSyncEnabled: Boolean(calendarSyncEnabledEmployeeIds?.has(employeeId)),
        timedEvents,
        externalBusyIntervals,
        parallelLaneCount,
        routeEdges,
        color: employeeColors[employeeId] ?? "var(--kontext-accent)",
        availability: availability
          ? {
              startMinutes: availability.startMinutes,
              endMinutes: availability.endMinutes,
              windows: availability.windows,
              dayOff: availability.dayOff,
            }
          : null,
      }
    })
  })
}

export const buildRouteEdges = ({
  transitions,
  employeeId,
  dayKey,
}: {
  transitions: CalendarRouteTransition[]
  employeeId: string
  dayKey: string
}): BoardRouteEdge[] => {
  return transitions
    .filter((item) => item.employee_id === employeeId && item.day === dayKey)
    .map((item) => {
      const gapStart = new Date(item.gap_start_at)
      const gapEnd = new Date(item.gap_end_at)
      const distanceLabel = item.missing_coordinates
        ? "nicht berechenbar"
        : typeof item.distance_km === "number"
          ? `${item.distance_km.toFixed(1)} km`
          : formatRoundedTravelMinutesLabel(item.rounded_travel_minutes ?? 10)

      return {
        id: item.id,
        employeeId: item.employee_id,
        day: item.day,
        sequenceIndex: item.sequence_index ?? 0,
        relationKind: item.relation_kind ?? "EVENT_TO_EVENT",
        previousEventId: item.previous_event_id,
        nextEventId: item.next_event_id,
        previousAnchorKind: item.previous_anchor_kind ?? "EVENT",
        nextAnchorKind: item.next_anchor_kind ?? "EVENT",
        previousAnchorStart: new Date(item.previous_anchor_start_at),
        previousAnchorEnd: new Date(item.previous_anchor_end_at),
        nextAnchorStart: new Date(item.next_anchor_start_at),
        nextAnchorEnd: new Date(item.next_anchor_end_at),
        previousEventType: item.previous_event_type,
        nextEventType: item.next_event_type,
        previousEventLabel: item.previous_event_label,
        nextEventLabel: item.next_event_label,
        previousWorkOrderNumber: item.previous_work_order_number,
        nextWorkOrderNumber: item.next_work_order_number,
        gapStart,
        gapEnd,
        gapMinutes: item.gap_minutes ?? 0,
        overlapMinutes: item.overlap_minutes ?? 0,
        distanceKm: item.distance_km,
        estimatedTravelMinutes: item.estimated_travel_minutes ?? null,
        roundedTravelMinutes: item.rounded_travel_minutes ?? null,
        routeGrade: item.route_grade,
        missingCoordinates: Boolean(item.missing_coordinates),
        reason: item.reason,
        label: distanceLabel,
        tooltip: buildRouteTransitionTooltip(item),
      }
    })
}

/** Geometry is a projection only: it never changes event times or business durations. */
export const projectTimelineColumns = (
  columns: BoardColumn[],
  {
    dayStartHour,
    dayEndHour,
    slotMinutes,
    slotHeight,
    minEventHeight,
  }: Pick<
    BuildColumnsInput,
    "dayStartHour" | "dayEndHour" | "slotMinutes" | "slotHeight" | "minEventHeight"
  >,
): BoardColumn[] =>
  columns.map((column) => {
    const viewStart = setTime(column.day, dayStartHour, 0)
    const viewEnd = setTime(column.day, dayEndHour, 0)
    const position = (start: Date, end: Date) => {
      const from = Math.max(start.getTime(), viewStart.getTime())
      const to = Math.min(end.getTime(), viewEnd.getTime())

      return to > from
        ? {
            top: ((from - viewStart.getTime()) / 60000 / slotMinutes) * slotHeight,
            height: Math.max(minEventHeight, ((to - from) / 60000 / slotMinutes) * slotHeight),
          }
        : null
    }
    const timedEvents = column.timedEvents.flatMap((item) => {
      const geometry = position(item.start, item.end)

      return geometry ? [{ ...item, ...geometry }] : []
    })
    const externalBusyIntervals = (column.externalBusyIntervals ?? []).flatMap((item) => {
      const geometry = position(item.start, item.end)

      return geometry ? [{ ...item, ...geometry }] : []
    })

    return {
      ...column,
      timedEvents,
      externalBusyIntervals,
      parallelLaneCount: assignParallelLanes(timedEvents),
    }
  })

export const buildColumns = (input: BuildColumnsInput): BoardColumn[] =>
  projectTimelineColumns(buildDayColumns(input), input)
